import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import PageHeader from "@/components/layout/PageHeader";
import Seo from "@/components/Seo";
import { TEAM_MEMBERS } from "@/data/siteData";

const TeamPage = () => {
  return (
    <Layout>
      <Seo
        title="Екип"
        path="/ekip"
        description="Запознайте се с екипа на Outsource Point — професионалисти с дългогодишен опит в обслужването на клиенти и управлението на кол център кампании."
      />
      <PageHeader 
        subtitle="Нашият екип"
        title="Екип"
        description="Професионалисти с дългогодишен опит в обслужването на клиенти"
      />

      {/* Team Introduction */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              Хората зад успеха
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Нашият екип се състои от мотивирани и опитни професионалисти,
              които споделят общата ни цел – да предоставяме изключително
              обслужване на клиентите. Всеки член на екипа допринася с уникалните
              си умения и опит за успеха на компанията.
            </p>
          </div>

          {/* Team Grid */}
          <div className="space-y-8 lg:space-y-12">
            {/* Featured leadership card */}
            {TEAM_MEMBERS.length > 0 && (
              <div
                className="bg-gradient-to-br from-[#2E4A91] to-[#243b75] rounded-3xl p-8 lg:p-12 flex flex-col md:flex-row gap-8 items-center shadow-xl"
                data-testid="team-card-0"
              >
                <img
                  src={TEAM_MEMBERS[0].image}
                  alt={TEAM_MEMBERS[0].name}
                  className="w-44 h-44 lg:w-56 lg:h-56 rounded-2xl object-cover object-top shadow-2xl flex-shrink-0"
                />
                <div className="text-center md:text-left">
                  <span className="inline-block text-xs font-semibold text-[#2ED573] uppercase tracking-wider mb-2">
                    Ръководство
                  </span>
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                    {TEAM_MEMBERS[0].name}
                  </h3>
                  <p className="text-[#2ED573] font-medium mb-4 text-lg">
                    {TEAM_MEMBERS[0].position}
                  </p>
                  <p className="text-slate-200 leading-relaxed lg:text-lg">
                    {TEAM_MEMBERS[0].fullDescription || TEAM_MEMBERS[0].description}
                  </p>
                </div>
              </div>
            )}

            {/* Remaining team members */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {TEAM_MEMBERS.slice(1).map((member, index) => (
                <div
                  key={index}
                  className="bg-slate-50 rounded-2xl p-6 lg:p-8 flex flex-col md:flex-row gap-6 items-start card-hover"
                  data-testid={`team-card-${index + 1}`}
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 md:w-40 md:h-40 rounded-xl object-cover object-top shadow-lg flex-shrink-0"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-[#2E4A91] mb-1">
                      {member.name}
                    </h3>
                    <p className="text-[#2ED573] font-medium mb-4">
                      {member.position}
                    </p>
                    <p className="text-slate-600 leading-relaxed">
                      {member.fullDescription || member.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold text-[#2ED573] uppercase tracking-wider">
                Нашата култура
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                Екипът като семейство
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                В Outsource Point вярваме, че добрата работна среда е ключът към
                успеха. Нашият екип работи като едно голямо семейство, където
                всеки се чувства ценен и подкрепен.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Насърчаваме професионалното развитие, предоставяме възможности за
                обучение и създаваме условия за работа, които позволяват на
                служителите ни да дадат най-доброто от себе си.
              </p>
              <Link to="/kontakti">
                <Button className="btn-primary">
                  Присъединете се към нас
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
                alt="Екипна работа"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="bg-gradient-to-r from-[#2E4A91] to-[#243b75] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Искате да станете част от екипа?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Разгледайте нашите отворени позиции и започнете кариерата си в
            Outsource Point
          </p>
          <Link to="/kontakti">
            <Button className="bg-white text-[#2E4A91] hover:bg-slate-100 text-lg px-8 h-14 rounded-lg font-semibold">
              Свържете се с нас
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default TeamPage;
